import React, { useEffect, useState } from 'react';
import { Plus, Edit3, Trash2, X, Save, Eye, EyeOff } from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import UploadButton from './UploadButton';
import RichTextEditor from './RichTextEditor';

const empty = { category: 'Duyuru', title: '', excerpt: '', content: '', image: '', date: '', published: true };

const CATEGORIES = ['Duyuru', 'Haber', 'Etkinlik', 'Burs', 'Proje', 'Gala', 'Konser', 'Seminer', 'Kampanya'];

export default function AdminNews() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const reload = () => api.adminListNews().then(setItems).catch(() => setItems([]));
  useEffect(() => { reload(); }, []);

  const save = async () => {
    try {
      if (editing.id) {
        const { id, slug, created_at, updated_at, ...payload } = editing;
        await api.adminUpdateNews(id, payload); toast.success('Güncellendi');
      } else {
        await api.adminCreateNews(editing); toast.success('Eklendi');
      }
      setEditing(null); reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Hata');
    }
  };
  const del = async (id) => {
    if (!window.confirm('Sil?')) return;
    await api.adminDeleteNews(id); toast.success('Silindi'); reload();
  };

  return (
    <div data-testid="admin-news">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-bold">Duyurular</div>
          <h1 className="font-display text-4xl mt-2 leading-none">Haber & Duyurular</h1>
          <p className="mt-2 text-ink-400 text-sm">{items.length} kayıt</p>
        </div>
        <button onClick={() => setEditing({ ...empty, date: new Date().toLocaleDateString('tr-TR') })} className="bg-ink-600 text-cream rounded-full px-5 h-11 text-sm font-semibold hover:bg-ink-500 inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Yeni Duyuru</button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(n => (
          <div key={n.id} className="group bg-white rounded-2xl border border-ink-100 overflow-hidden hover:border-gold transition-colors">
            {n.image && <div className="aspect-[4/3] overflow-hidden bg-ink-50"><img src={n.image} alt="" className="w-full h-full object-cover" /></div>}
            <div className="p-4">
              <div className="text-[10px] uppercase tracking-widest text-coral-300 font-bold">{n.category} · {n.date}</div>
              <h3 className="font-display text-lg mt-1 leading-snug truncate">{n.title}</h3>
              <p className="text-xs text-ink-400 line-clamp-2 mt-1">{n.excerpt}</p>
              <div className="mt-3 flex items-center gap-2">
                <button onClick={() => setEditing(n)} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-ink-600 text-cream rounded-lg px-3 h-9 text-xs font-semibold"><Edit3 className="w-3.5 h-3.5" /> Düzenle</button>
                <button onClick={() => del(n.id)} className="w-9 h-9 grid place-items-center rounded-lg bg-coral-50 text-coral-300 hover:bg-coral-100"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-ink-900/70 backdrop-blur-sm grid place-items-start justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl w-full max-w-4xl my-8" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b border-ink-100 flex items-center justify-between rounded-t-2xl">
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-coral-300 font-bold">Duyuru</div>
                <h2 className="font-display text-2xl mt-0.5">{editing.id ? 'Düzenle' : 'Yeni Duyuru'}</h2>
              </div>
              <button onClick={() => setEditing(null)} className="w-9 h-9 grid place-items-center hover:bg-cream-200 rounded-lg" title="Kapat (Esc)"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-5">
              <div className="grid md:grid-cols-3 gap-4">
                <Field label="Kategori">
                  <input list="news-cat-list" value={editing.category} onChange={e => setEditing({ ...editing, category: e.target.value })} className="inp w-full" />
                  <datalist id="news-cat-list">{CATEGORIES.map(c => <option key={c} value={c} />)}</datalist>
                </Field>
                <Field label="Tarih (gösterim)">
                  <input value={editing.date} onChange={e => setEditing({ ...editing, date: e.target.value })} placeholder="12 Ekim 2026" className="inp w-full" />
                </Field>
                <Field label="Durum">
                  <button onClick={() => setEditing({ ...editing, published: !editing.published })} className={`w-full h-10 inline-flex items-center justify-center gap-2 rounded-xl text-sm font-semibold ${editing.published ? 'bg-emerald-50 text-emerald-700' : 'bg-coral-50 text-coral-300'}`}>{editing.published ? <><Eye className="w-4 h-4" /> Yayında</> : <><EyeOff className="w-4 h-4" /> Taslak</>}</button>
                </Field>
              </div>
              <Field label="Başlık *">
                <input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} placeholder="Örn: Yıl Sonu Burs Töreni" className="inp w-full text-base font-semibold" />
              </Field>
              <Field label="Kapak Görseli">
                <div className="flex gap-2">
                  <input value={editing.image} onChange={e => setEditing({ ...editing, image: e.target.value })} placeholder="https:// veya /uploads/..." className="inp flex-1" />
                  <UploadButton onUploaded={url => setEditing({ ...editing, image: url })} />
                </div>
                {editing.image && <img src={editing.image} alt="" className="mt-2 max-h-48 rounded-xl border border-ink-100" />}
              </Field>
              <Field label="Özet (önizlemede görünen kısa metin)">
                <textarea rows={2} value={editing.excerpt} onChange={e => setEditing({ ...editing, excerpt: e.target.value })} placeholder="1-2 cümlelik özet..." className="inp w-full py-2" />
              </Field>
              <Field label="İçerik (Zengin Metin Editörü)">
                <RichTextEditor
                  value={editing.content}
                  onChange={(html) => setEditing({ ...editing, content: html })}
                  placeholder="Duyurunuzun tam içeriğini buraya yazın. Başlık, liste, bağlantı, alıntı, görsel ekleyebilirsiniz..."
                  minHeight={320}
                />
              </Field>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-ink-100 px-6 py-4 flex justify-end gap-2 rounded-b-2xl">
              <button onClick={() => setEditing(null)} className="px-5 h-11 rounded-full border border-ink-200 text-ink-500 text-sm font-semibold hover:bg-cream-200">İptal</button>
              <button onClick={save} data-testid="news-save" className="bg-gold text-ink-700 rounded-full px-6 h-11 text-sm font-semibold hover:bg-gold-400 inline-flex items-center gap-2"><Save className="w-4 h-4" /> Kaydet</button>
            </div>
          </div>
        </div>
      )}

      <style>{`.inp{ background:#F4EEE2; border:1px solid #E8DECF; border-radius:12px; padding:0 12px; height:40px; font-size:14px; outline:none; }.inp:focus{ border-color:#0F1E4D }.inp.w-full{ width:100% }`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] tracking-[0.25em] uppercase text-ink-400 font-bold">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
